import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useBackupModal } from '@/hooks/useBackupModal';

interface NavItem {
  path: string;
  label: string;
  icon: string;
}

const navItems: NavItem[] = [
  { path: '/', label: 'Dashboard', icon: 'ri-dashboard-line' },
  { path: '/clientes', label: 'Clientes', icon: 'ri-building-2-line' },
  { path: '/catalogo', label: 'Catálogo', icon: 'ri-store-2-line' },
  { path: '/suscripciones', label: 'Suscripciones', icon: 'ri-refresh-line' },
  { path: '/cobranza', label: 'Cobranza', icon: 'ri-bill-line' },
];

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { openBackup } = useBackupModal();

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <aside
      className={`h-screen sticky top-0 flex flex-col bg-background-900 text-foreground-100 transition-all duration-300 z-40 ${
        collapsed ? 'w-16' : 'w-60'
      }`}
    >
      <div className="flex items-center h-16 px-4 border-b border-background-700/50">
        {!collapsed && (
          <div className="flex items-center gap-3 overflow-hidden whitespace-nowrap">
            <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center flex-shrink-0">
              <i className="ri-radar-line text-white text-lg"></i>
            </div>
            <div className="flex flex-col leading-tight">
              <span className="text-sm font-bold tracking-tight text-white">Telemetry</span>
              <span className="text-[10px] font-medium text-primary-300 tracking-widest uppercase">ERP</span>
            </div>
          </div>
        )}
        {collapsed && (
          <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center mx-auto flex-shrink-0">
            <i className="ri-radar-line text-white text-lg"></i>
          </div>
        )}
      </div>

      <button
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-20 w-6 h-6 rounded-full bg-background-700 border border-background-600 flex items-center justify-center cursor-pointer hover:bg-background-600 transition-colors z-50"
      >
        <i className={`${collapsed ? 'ri-arrow-right-s-line' : 'ri-arrow-left-s-line'} text-foreground-300 text-xs`}></i>
      </button>

      <nav className="flex-1 py-4 sidebar-scroll overflow-y-auto">
        <ul className="flex flex-col gap-1 px-2">
          {navItems.map((item) => (
            <li key={item.path}>
              <button
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap group ${
                  isActive(item.path)
                    ? 'bg-primary-600 text-white shadow-lg shadow-primary-900/30'
                    : 'text-foreground-300 hover:bg-background-800 hover:text-foreground-100'
                }`}
                title={collapsed ? item.label : undefined}
              >
                <i className={`${item.icon} text-lg flex-shrink-0 ${isActive(item.path) ? '' : 'group-hover:text-foreground-100'}`}></i>
                {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
                {isActive(item.path) && !collapsed && (
                  <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white"></div>
                )}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      <div className="px-2 pb-2">
        <button
          onClick={openBackup}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap text-foreground-300 hover:bg-background-800 hover:text-foreground-100 group border border-dashed border-background-700/70"
          title={collapsed ? 'Respaldo de Base de Datos' : undefined}
        >
          <i className="ri-database-2-line text-lg flex-shrink-0 group-hover:text-primary-300 transition-colors"></i>
          {!collapsed && (
            <span className="text-sm font-medium flex flex-col items-start leading-tight">
              <span>Respaldo de Base de Datos</span>
              <span className="text-[10px] text-foreground-500 font-normal">Exportar .ZIP</span>
            </span>
          )}
        </button>
      </div>

      <div className="p-3 border-t border-background-700/50">
        {!collapsed ? (
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-400 to-accent-400 flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs font-bold">JD</span>
            </div>
            <div className="flex flex-col overflow-hidden">
              <span className="text-xs font-medium text-foreground-200 truncate">Juan Domínguez</span>
              <span className="text-[10px] text-foreground-500">Administrador</span>
            </div>
          </div>
        ) : (
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-400 to-accent-400 flex items-center justify-center mx-auto">
            <span className="text-white text-xs font-bold">JD</span>
          </div>
        )}
      </div>
    </aside>
  );
}